import React, { useState, useEffect, useMemo } from 'react';
import { Modal } from '@/components/UI/Modal';
import { Input } from '@/components/UI/Input';
import { Button } from '@/components/UI/Button';
import { Spinner } from '@/components/UI/Spinner';
import { Alert } from '@/components/UI/Alert';
import {
  PlatformTenant,
  PlatformUser,
  useListPlatformUsersQuery,
  useClonePlatformTenantMutation,
} from '@/redux/internal-services/platformAdminApi';

interface Props {
  sourceTenant: PlatformTenant;
  onClose: () => void;
}

const ROLE_PRIORITY = [
  'tenant_admin',
  'project_manager',
  'office_admin',
  'sales_agent',
  'crew_member',
];

const ROLE_LABELS: Record<string, string> = {
  tenant_admin: 'Tenant Admin',
  project_manager: 'Project Manager',
  office_admin: 'Office Admin',
  sales_agent: 'Sales Agent',
  crew_member: 'Crew Member',
};

interface UserFormState {
  name: string;
  email: string;
  password: string;
  showPassword: boolean;
}

const CloneTenantModal: React.FC<Props> = ({ sourceTenant, onClose }) => {
  const [companyName, setCompanyName] = useState(`${sourceTenant.company_name} (Clone)`);
  const [userForms, setUserForms] = useState<Record<string, UserFormState>>({});
  const [error, setError] = useState<string | null>(null);

  const { data: usersData, isLoading: usersLoading } = useListPlatformUsersQuery(
    { tenant_id: sourceTenant.tenant_id },
    { refetchOnMountOrArgChange: true }
  );

  const [cloneTenant, { isLoading: cloning }] = useClonePlatformTenantMutation();

  const users: PlatformUser[] = useMemo(
    () => {
      const s = (u: PlatformUser) => u.status?.toLowerCase() ?? '';
      return (usersData?.data ?? []).filter(
        (u) => s(u) !== 'deactivated' && s(u) !== 'deleted' && s(u) !== 'suspended',
      );
    },
    [usersData],
  );

  // Initialize form state once users load
  useEffect(() => {
    if (users.length === 0) return;
    setUserForms((prev) => {
      const next = { ...prev };
      for (const u of users) {
        if (!next[u.user_id]) {
          next[u.user_id] = {
            name: u.name ?? '',
            email: u.email ?? '',
            password: '123456789',
            showPassword: true,
          };
        }
      }
      return next;
    });
  }, [users]);

  // Group users by primary_role_key
  const groupedUsers = useMemo(() => {
    const groups: Record<string, PlatformUser[]> = {};
    for (const u of users) {
      const key = u.primary_role_key ?? 'other';
      if (!groups[key]) groups[key] = [];
      groups[key].push(u);
    }
    // Sort keys by priority order
    const priorityKeys = ROLE_PRIORITY.filter((k) => groups[k]);
    const otherKeys = Object.keys(groups)
      .filter((k) => !ROLE_PRIORITY.includes(k))
      .sort();
    return [...priorityKeys, ...otherKeys].map((key) => ({
      key,
      label: ROLE_LABELS[key] ?? key,
      users: groups[key],
    }));
  }, [users]);

  const setField = (userId: string, field: keyof UserFormState, value: string | boolean) => {
    setUserForms((prev) => ({
      ...prev,
      [userId]: { ...prev[userId], [field]: value },
    }));
  };

  const validate = (): string | null => {
    if (!companyName.trim()) return 'Company name is required.';

    const emails: string[] = [];
    for (const u of users) {
      const form = userForms[u.user_id];
      if (!form) continue;
      if (!form.name.trim()) return `Enter a name for the cloned account of "${u.name ?? u.user_id}".`;
      if (!form.email.trim()) return `Email is required for ${u.name ?? u.user_id}.`;
      if (!form.email.includes('@')) return `Invalid email for ${u.name ?? u.user_id}.`;
      if (form.password.length < 8) return `Password must be at least 8 characters for ${u.name ?? u.user_id}.`;
      if (emails.includes(form.email.toLowerCase())) return `Duplicate email: ${form.email}`;
      emails.push(form.email.toLowerCase());
    }
    return null;
  };

  const handleSubmit = async () => {
    setError(null);
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      await cloneTenant({
        tenantId: sourceTenant.tenant_id,
        body: {
          company_name: companyName.trim(),
          users: users.map((u) => ({
            source_user_id: u.user_id,
            name: userForms[u.user_id]?.name?.trim() ?? '',
            email: userForms[u.user_id]?.email?.trim() ?? '',
            password: userForms[u.user_id]?.password ?? '',
          })),
        },
      }).unwrap();
      onClose();
    } catch (err: unknown) {
      const e = err as { data?: { detail?: string | Array<{ msg: string }>; message?: string } };
      const raw = e?.data?.detail;
      const msg = Array.isArray(raw)
        ? raw.map((d) => d.msg).join('; ')
        : raw ?? e?.data?.message ?? 'Clone failed. Please try again.';
      setError(msg);
    }
  };

  const footer = (
    <div className="flex flex-col gap-3">
      {error && (
        <Alert tone="danger">
          {error}
        </Alert>
      )}
      <div className="flex items-center justify-end gap-3">
        <Button variant="ghost" size="sm" onClick={onClose} disabled={cloning}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={handleSubmit}
          disabled={cloning || usersLoading}
        >
          {cloning ? <Spinner size="sm" className="mr-2" /> : null}
          {cloning ? 'Cloning…' : 'Clone Tenant'}
        </Button>
      </div>
    </div>
  );

  return (
    <Modal
      title={`Clone Tenant: ${sourceTenant.company_name}`}
      onClose={onClose}
      maxWidthClassName="max-w-3xl"
      closeOnBackdrop={false}
      footer={footer}
      bodyClassName="space-y-6 px-5 py-4"
    >
      {/* Company name */}
      <div>
        <label className="mb-1.5 block text-sm font-medium text-ui-text">New Company Name</label>
        <Input
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
          placeholder="Company name"
        />
      </div>

      {/* Users by role */}
      {usersLoading ? (
        <div className="flex justify-center py-6">
          <Spinner size="md" />
        </div>
      ) : (
        <div className="space-y-5">
          <div>
            <p className="text-sm font-medium text-ui-text">User Mapping</p>
            <p className="mt-0.5 text-xs text-ui-text3">
              Each user from the source tenant will be re-created in the cloned tenant with their role preserved.
              Set new credentials for each account below.
            </p>
          </div>
          {/* Column headers — shown once above all role groups */}
          <div className="-mt-2 hidden sm:block">
            <div className="grid grid-cols-[1fr_auto_1fr_1fr_1fr] gap-2 px-3 mb-0.5">
              <p className="text-xs text-ui-text3">Source account</p>
              <span className="invisible">→</span>
              <p className="col-span-3 text-xs font-medium text-ui-text2 border-b border-ui-border2 pb-0.5">
                New account
              </p>
            </div>
            <div className="grid grid-cols-[1fr_auto_1fr_1fr_1fr] gap-2 px-3">
              <span />
              <span className="invisible">→</span>
              <p className="text-xs text-ui-text3">Name</p>
              <p className="text-xs text-ui-text3">Email</p>
              <p className="text-xs text-ui-text3">Password</p>
            </div>
          </div>
          {groupedUsers.map(({ key, label, users: roleUsers }) => (
            <div key={key}>
              <div className="mb-2 flex items-center gap-2">
                <span className="eyebrow">{label}</span>
                {roleUsers.length > 1 && (
                  <span className="rounded-full bg-ui-raised px-2 py-0.5 text-xs text-ui-text3">
                    {roleUsers.length}
                  </span>
                )}
              </div>

              <div className="space-y-3">
                {roleUsers.map((u) => {
                  const form = userForms[u.user_id] ?? { name: '', email: '', password: '', showPassword: false };
                  return (
                    <div
                      key={u.user_id}
                      className="grid grid-cols-1 gap-2 rounded-control border border-ui-border bg-ui-card p-3 sm:grid-cols-[1fr_auto_1fr_1fr_1fr]"
                    >
                      {/* Source (read-only) */}
                      <div className="flex flex-col justify-center">
                        <div className="flex items-center gap-1.5">
                          <p className="text-sm font-medium text-ui-text2">{u.name ?? '—'}</p>
                          {u.status?.toLowerCase() === 'invited' && (
                            <span className="rounded-full bg-warning/15 px-1.5 py-0.5 text-[10px] font-medium text-warning-text">
                              Invited
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-ui-text3">{u.email ?? ''}</p>
                      </div>

                      {/* Arrow separator */}
                      <div className="hidden items-center text-ui-text3 sm:flex">→</div>

                      {/* New name */}
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-ui-text3 sm:hidden">New name</label>
                        <Input
                          value={form.name}
                          onChange={(e) => setField(u.user_id, 'name', e.target.value)}
                        />
                      </div>

                      {/* New email */}
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-ui-text3 sm:hidden">New email</label>
                        <Input
                          type="email"
                          value={form.email}
                          onChange={(e) => setField(u.user_id, 'email', e.target.value)}
                        />
                      </div>

                      {/* Password with show/hide */}
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-ui-text3 sm:hidden">Password</label>
                        <div className="relative">
                          <Input
                            type={form.showPassword ? 'text' : 'password'}
                            value={form.password}
                            onChange={(e) => setField(u.user_id, 'password', e.target.value)}
                            className="pr-9"
                          />
                          <button
                            type="button"
                            aria-label={form.showPassword ? 'Hide password' : 'Show password'}
                            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-icon hover:text-ui-text2 focus-visible:outline-none"
                            onClick={() => setField(u.user_id, 'showPassword', !form.showPassword)}
                          >
                            {form.showPassword ? (
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                                strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
                                <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
                                <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
                                <line x1="1" y1="1" x2="23" y2="23" />
                              </svg>
                            ) : (
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                                strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                <circle cx="12" cy="12" r="3" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};

export default CloneTenantModal;
